"use client";

import {
  motion,
  useScroll,
  useTransform,
  useSpring,
} from "framer-motion";
import {
  Layers,
  MessageSquare,
  Cpu,
  BarChart3,
  ShieldCheck,
  Flag,
  LucideIcon
} from "lucide-react";
import { useRef } from "react";

interface RoadmapStepProps {
  phase: string;
  time: string;
  title: string;
  items: string[];
  icon: LucideIcon;
  index: number;
  progress: any;
  total: number;
}

const RoadmapStep = ({
  phase,
  time,
  title,
  items,
  icon: Icon,
  index,
  progress,
  total
}: RoadmapStepProps) => {

  const start = index / total;
  const end = (index + 1) / total;

  const opacity = useTransform(progress, [start, end], [0.3, 1]);
  const y = useTransform(progress, [start, end], [80, 0]);
  const scale = useTransform(progress, [start, end], [0.96, 1]);
  const glow = useTransform(progress, [start, end], [0, 1]);

  const boxShadow = useTransform(
    glow,
    (g) => `
      0 0 ${g * 20}px rgba(59,130,246,0.4),
      0 0 ${g * 60}px rgba(59,130,246,0.25),
      0 0 ${g * 120}px rgba(59,130,246,0.15)
    `
  )

  const smoothY = useSpring(y, { stiffness: 80, damping: 20 });

  return (
    <div className="relative flex gap-10 pb-32">
      <div className="flex flex-col items-center relative">
        <motion.div
          style={{ scale, boxShadow }}
          className="relative w-14 h-14 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-slate-500 z-10"
        >
          <Icon size={24} strokeWidth={1.5} />
        </motion.div>
      </div>

      <motion.div
        style={{ opacity, y: smoothY, scale }}
        className="flex-grow"
      >
        <div className="flex items-center gap-4 mb-4">
          <span className="text-[10px] font-mono font-bold text-blue-600 uppercase tracking-[0.3em] px-3 py-1 rounded-full bg-blue-50 border border-blue-200">
            {phase}
          </span>
          <span className="text-slate-400 font-mono text-xs tracking-widest">
            {time}
          </span>
        </div>

        <h3 className="text-3xl font-bold mb-6 tracking-tight text-slate-900">
          {title}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-3 text-slate-600">
              <div className="h-[1px] w-4 bg-slate-300" />
              <span className="text-sm font-light">{item}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

const RoadmapSection = () => {
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 80,
    damping: 20
  });

  const lineHeight = useTransform(
    smoothProgress,
    [0, 1],
    ["0%", "100%"]
  );

  const roadmapData = [
    {
      phase: "Phase 0 – Foundation",
      time: "0–1 Months",
      title: "Foundation",
      icon: Layers,
      items: [
        "Domain identity integration",
        "Core workspace framework",
        "Granular RBAC architecture",
        "Audit logging systems"
      ]
    },
    {
      phase: "Phase 1 – Communication Infrastructure",
      time: "1–3 Months",
      title: "Communication Infrastructure",
      icon: MessageSquare,
      items: [
        "Encrypted mail and messaging",
        "Knowledge wiki deployment",
        "Developer API key system",
        "Webhook infrastructure"
      ]
    },
    {
      phase: "Phase 2 – Intelligence Integration",
      time: "3–6 Months",
      title: "Intelligence Integration",
      icon: Cpu,
      items: [
        "Agent Studio framework",
        "Automation orchestration",
        "Cross-tool AI integration",
        "Neural search layer"
      ]
    },
    {
      phase: "Phase 3 – Commercial Infrastructure",
      time: "6–9 Months",
      title: "Commercial Infrastructure",
      icon: BarChart3,
      items: [
        "Payment processing architecture",
        "Merchant dashboard system",
        "Loyalty protocol",
        "Affiliate console"
      ]
    },
    {
      phase: "Phase 4 – Enterprise Resilience",
      time: "9–12 Months",
      title: "Enterprise Resilience",
      icon: ShieldCheck,
      items: [
        "Security operations center",
        "Data sovereignty controls",
        "Scalability cluster management",
        "Compliance suite deployment"
      ]
    }
  ];

  return (
    <section
     id="roadmap"
      ref={containerRef}
      className="relative py-40 bg-[#F8F9FF]"
      style={{ perspective: "2000px" }}
    > 
   
      {/* <div className="container mx-auto px-6 max-w-6xl relative"> */}
      <div className="container relative mx-auto px-6">
        {/* Vertical progress line */}
        <div className="absolute left-[28px] top-[50px] bottom-0 w-[2px] bg-slate-200">
    <motion.div
      style={{ height: lineHeight }}
      className="w-full bg-gradient-to-b from-blue-500 via-blue-400 to-transparent"
    />
  </div>

       
      <div className="max-w-4xl mb-24 ml-2">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              className="inline-block rounded-full px-4 py-1.5
              font-mono border border-indigo-100
              bg-indigo-50 text-[10px]
              font-bold tracking-[0.3em]
              uppercase mb-4 text-blue-600"
            >
              Road map
            </motion.div>
  
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="text-5xl md:text-7xl tracking-tighter mb-8"
            >
              Development Trajectory
            </motion.h2>
  
            <p className="text-slate-600 text-lg md:text-xl font-light max-w-2xl">
            12-Month Infrastructure Roadmap
            </p>
          </div>
        {/* Steps */}
        <div className="relative ml-12">
          {roadmapData.map((step, index) => (
            <RoadmapStep
              key={index}
              {...step}
              index={index}
              total={roadmapData.length}
              progress={smoothProgress}
            />
          ))}
        </div>

        {/* 24-Month Objective */}
        <div className="mt-40 p-14 ml-2 rounded-[3rem] border border-blue-200 bg-blue-50">
          <div className="flex items-center gap-10">
            <div className="w-24 h-24 rounded-3xl bg-blue-600 text-white flex items-center justify-center shadow-xl">
              <Flag size={36} />
            </div>
            <div>
              <h4 className="text-3xl font-bold mb-4 text-slate-900">
                24-Month Objective
              </h4>
              <p className="text-slate-500 text-lg font-light">
                Gao Workspace evolves into a stable operational infrastructure layer
                where identity, payments, and intelligence operate within a unified
                and governed digital environment.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoadmapSection;